import AutorenewIcon from "@mui/icons-material/Autorenew";
import CastForEducation from "@mui/icons-material/CastForEducation";
import Delete from "@mui/icons-material/Delete";
import MoreVert from "@mui/icons-material/MoreVert";
import { Avatar, Box, Chip, Grid, IconButton, Menu, Tooltip as MuiTooltip, TextField } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import type React from "react";
import { type ChangeEventHandler, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { clearNurseHighlight, highlightNurseId, useNurseHighlight } from "../../contexts/NurseHighlight";
import { useSelfScheduling } from "../../contexts/SelfScheduling/SelfSchedulingContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { isDefaultShiftTime } from "../../contexts/utils/shift-times";
import { useWeekViewContext } from "../../contexts/WeekView";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useIsMobile } from "../../hooks/useMobile";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useRoleColors } from "../../hooks/useRoleColors";
import { useStaffSchedulePath } from "../../hooks/useStaffSchedulePath";
import { INK, SURFACE } from "../../theme/tokens";
import { withPropsDebugging } from "../../utils/DebugComponent";
import { checkIsDraftMode } from "../../utils/demoDraftWeeks";
import { findScheduleSlot } from "../../utils/departmentWeeks";
import { AbsenceType, ChangesSource, type NurseInShift, NurseTrainingStatus, type ShiftPerson } from "../../utils/models";
import axiosInstance from "../../utils/networkWrapper";
import { HeaderButton } from "../headerButton";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { NormalizedNurseMenu } from "../NormalizedNurseMenu/NormalizedNurseMenu";
import { ThemedSvg } from "../UIToolkit/ThemedSvg";
import Style from "./NurseContainer.module.scss";
import { areRolesMatch, compareMessages, indexToDay, indexToDayPart, PendingDiscardKind, pendingDiscardOf } from "./utils";

let mousePosition = { x: 0, y: 0 };

const isAnyMenuOpen = { current: false };

type Props = {
	date: DateTime;
	shiftType: string;
	nurseInShift: NurseInShift;
	swapWith?: number;
	isPending: boolean;
	isPendingToDelete: boolean;
	isPendingToSwap: boolean;
	isPendingToMove: boolean;
	isPublished: boolean;
	nurseData: ShiftPerson;
	message: string;
	isAdd: boolean;
	canBeReliefCharge?: boolean;
	highlightedNurseId?: NurseInShift["nurseId"];
};

type SlotProps = Props & {
	weekIndex: number;
	shiftNo: number;
	dayNo: number;
};

export const NurseContainer = memo((props: Props) => {
	const { scheduleWeeks } = useWeekViewContext();
	const slot = findScheduleSlot(scheduleWeeks, props.date, props.shiftType);
	// Rendered only inside a shown day's block.
	if (!slot) return null;
	return <NurseContainerForSlot {...props} weekIndex={slot.weekIndex} shiftNo={slot.shiftIndex} dayNo={slot.dayIndex} />;
});

const NurseContainerForSlot = memo(
	({
		date,
		shiftType,
		nurseInShift,
		weekIndex,
		shiftNo,
		dayNo,
		isPending,
		isPendingToDelete,
		isPendingToSwap,
		isPublished,
		nurseData,
		isAdd,
		message,
		canBeReliefCharge,
		highlightedNurseId,
	}: SlotProps) => {
		const roleColor = useRoleColors();
		const { userInfo } = useAuth();
		const { isMobile } = useIsMobile();
		const { t } = useTranslation();
		const navigate = useNavigate();
		const { isNurseIdHighlighted } = useNurseHighlight(nurseInShift.nurseId as number);
		const {
			setSwapNurseId,
			setSwapNurseWithId,
			swapNurseId,
			swapDayNo,
			swapShiftNo,
			setSwapDayNo,
			isSnf,
			setSwapDayNoWith,
			setSwapShiftNo,
			setSwapShiftNoWith,
			acceptNurseChanges,
			declineNurseChanges,
			addMessageToNurse,
			isAutoScheduler,
			isLowry,
			chosenDepartment,
			showErrorSnackbar,
			getShiftTimes,
			isHospitalDemo,
			restoreNurseToSpecificDate,
			getWeekIndexByDate,
		} = useHospitalData();
		const { scheduleWeeks, getSchedulePerson } = useWeekViewContext();
		const formatPersonName = useFormatPersonName();
		const week = scheduleWeeks[weekIndex];
		const { isDataInSelfSchedulingPeriod } = useSelfScheduling();
		const { discardNurseMove, discardNurseAdd, discardNurseRemove } = useHandleNurseChange();
		const prevDeleteValueRef = useRef(isPendingToDelete);
		const prevSwapValueRef = useRef(isPendingToSwap);
		const [editMessage, setEditMessage] = useState(message);
		const [editMessageReminder, setEditMessageReminder] = useState("");
		const [menuOpenReminder, setMenuOpenReminder] = useState(false);
		const elementRef = useRef<HTMLDivElement>(null);
		const [isMouseOver, setIsMouseOver] = useState(false);
		const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
		const { showSnackbar } = useSnackbar();
		const [imageUrl, setImageUrl] = useState("");

		const [isHighlighted, setIsHighlighted] = useState(false);
		const [isSwapWithItemCandidate, setIsSwapWithItemCandidate] = useState(false);
		const shouldHighlight = isHighlighted || (isNurseIdHighlighted && swapNurseId === -1) || nurseInShift.nurseId === highlightedNurseId;
		const shouldSwapHighlight = nurseInShift.nurseId === swapNurseId && dayNo === swapDayNo && shiftNo === swapShiftNo;
		const inDifferentGroup = shiftNo === swapShiftNo ? dayNo !== swapDayNo : true;
		const isDayIncludesNurse = useMemo(
			() => (swapNurseId !== -1 ? week.weeklyShifts[shiftNo]?.days[dayNo]?.nurses?.some((n: NurseInShift) => n.nurseId === swapNurseId) : false),
			[week, shiftNo, dayNo, swapNurseId],
		);
		const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<null | HTMLElement>(null);

		const isExistInSwap = useMemo(
			() => week.weeklyShifts[swapShiftNo]?.days[swapDayNo]?.nurses?.some((n: NurseInShift) => n.nurseId === nurseInShift.nurseId),
			[week, swapShiftNo, swapDayNo, nurseInShift.nurseId],
		);
		const swappedNurseRole = useMemo(() => getSchedulePerson(swapNurseId)?.role, [getSchedulePerson, swapNurseId]);
		const swappedRoleMatch = useMemo(
			() => swappedNurseRole !== undefined && areRolesMatch(swappedNurseRole, nurseData.role),
			[swappedNurseRole, nurseData.role],
		);

		const handleMouseEnter = useCallback(() => {
			if (actionsMenuAnchorEl || isAnyMenuOpen.current) {
				return;
			}
			// Don't handle mouse enter if parent container has staffing search open
			if (document.querySelector('[role="dialog"]')) {
				return;
			}

			if (nurseInShift.nurseId === swapNurseId || !inDifferentGroup || isDayIncludesNurse || (swappedNurseRole && !swappedRoleMatch)) {
				return;
			}

			if (swapNurseId === -1) {
				setIsHighlighted(true);
				highlightNurseId(nurseData.id);
			} else if (!isPendingToDelete) {
				setIsSwapWithItemCandidate(true);
			}
		}, [nurseInShift.nurseId, swapNurseId, inDifferentGroup, isDayIncludesNurse, setIsHighlighted, dayNo, isPendingToDelete, actionsMenuAnchorEl]);

		const handleAcceptChanges = () => {
			acceptNurseChanges(date, shiftType, nurseInShift.nurseId, false);
			setIsHighlighted(false);
			setIsSwapWithItemCandidate(false);
			clearNurseHighlight(nurseData.id);
			setIsMouseOver(false);
			setTimeout(() => {
				checkIfMouseOverElement();
			}, 100);
		};

		const handleDeclineChanges = async (isAutoScheduler = false) => {
			try {
				const pendingDiscard = pendingDiscardOf(nurseInShift);
				if (pendingDiscard.kind === PendingDiscardKind.Move) {
					const otherShiftTimes = getShiftTimes(week.weeklyShifts[shiftNo].days[pendingDiscard.originalDay], shiftNo, nurseInShift.nurseId);
					const otherNurse = week.weeklyShifts[shiftNo].days[pendingDiscard.originalDay].nurses.find((n) => n.nurseId === nurseInShift.nurseId);
					if (!otherNurse) throw new Error(`No other end found for the pending move of shift ${nurseInShift.id}`);
					await discardNurseMove({ nurse: nurseInShift, date, shiftType, otherShiftTimes, otherNurse });
				} else {
					if (pendingDiscard.kind === PendingDiscardKind.Remove) {
						await discardNurseRemove({ nurse: nurseInShift, date, shiftType });
						restoreNurseToSpecificDate(date, shiftType, nurseInShift.nurseId, false, isDataInSelfSchedulingPeriod(date));
					} else {
						await discardNurseAdd({ nurse: nurseInShift, date, shiftType });
						declineNurseChanges(date, shiftType, nurseInShift.nurseId, isAutoScheduler);
					}
				}
			} catch (e) {
				showErrorSnackbar("Failed to discard changes", { nurseId: nurseInShift.nurseId }, e);
				return;
			}

			setIsHighlighted(false);
			setIsSwapWithItemCandidate(false);
			clearNurseHighlight(nurseData.id);
			setIsMouseOver(false);
			setTimeout(() => {
				checkIfMouseOverElement();
			}, 100);
		};

		const handleMouseLeave = () => {
			setIsHighlighted(false);
			setIsSwapWithItemCandidate(false);
			clearNurseHighlight(nurseData.id);
		};

		const updateMessageIfNeeded = useCallback(() => {
			if (!message || (!prevDeleteValueRef.current && isPendingToDelete) || (!prevSwapValueRef.current && isPendingToSwap)) {
				const { weeklyShifts } = week;
				const { firstName } = nurseData;

				const day = indexToDay(dayNo);
				const date = weeklyShifts[shiftNo]?.days[dayNo]?.date?.toFormat("MM/DD");
				const time = indexToDayPart(shiftNo);

				let newMessage = "";
				let reminderMessage = "";
				if (isSnf) {
					if (isPendingToDelete) {
						newMessage = AbsenceType.NO_SHOW;
					}
				} else {
					newMessage = `Hi ${firstName}, ${userInfo.first_name} has requested that you pick up the ${day} (${date}) ${time} shift. Do you accept?`;
					reminderMessage = `Hi ${firstName}, We haven't received confirmation yet for ${day} (${date}) ${time} shift. If you have any questions, please reply to this message.`;
					if (isPendingToDelete) {
						newMessage = `Hi ${firstName}, ${userInfo.first_name} has canceled your ${day} (${date}) ${time} shift. If you have any questions, please reply to this message.`;
						reminderMessage = `Hi ${firstName}, we remind you that ${day} (${date}) ${time} shift is canceled. If you have any questions, please reply to this message.`;
					} else if (isPendingToSwap) {
						const isNurseInCurrentShift = weeklyShifts[shiftNo]?.days[dayNo]?.nurses.some(({ nurseId }) => nurseId === nurseInShift.nurseId);
						if (!isNurseInCurrentShift) {
							return;
						}

						const userSwapWith = nurseInShift.swapWith === undefined ? undefined : getSchedulePerson(nurseInShift.swapWith);
						const nurseSwapWithName = userSwapWith?.firstName;
						const dateSwapWith = weeklyShifts[nurseInShift.originalShift as any]?.days[nurseInShift.originalDay as any]?.date?.toFormat("MM/DD");

						const daySwapWith = indexToDay(nurseInShift.originalDay);
						const timeSwapWith = indexToDayPart(nurseInShift.originalShift);

						newMessage = `Hi ${firstName}, ${userInfo.first_name} has swapped your shift with ${nurseSwapWithName}, from ${day} (${date}) ${time} to ${daySwapWith} (${dateSwapWith}) ${timeSwapWith}. If you have any questions, please reply to this message.`;
						reminderMessage = `Hi ${firstName}, we remind you that ${day} (${date}) ${time} shift is cancled, your new shift is ${daySwapWith} (${dateSwapWith}) ${timeSwapWith}. If you have any questions, please reply to this message.`;
					}
				}

				setEditMessage(newMessage);
				setEditMessageReminder(reminderMessage);
			}

			prevDeleteValueRef.current = isPendingToDelete;
			prevSwapValueRef.current = isPendingToSwap;
		}, [
			dayNo,
			getSchedulePerson,
			week,
			isLowry,
			isPendingToDelete,
			isPendingToSwap,
			message,
			nurseData,
			nurseInShift.nurseId,
			nurseInShift.originalDay,
			nurseInShift.originalShift,
			nurseInShift.swapWith,
			shiftNo,
			userInfo.first_name,
		]);

		useEffect(() => {
			updateMessageIfNeeded();
		}, [updateMessageIfNeeded]);

		const onSwapClick = () => {
			if (swapNurseId !== -1 && nurseInShift.nurseId === swapNurseId) {
				setIsHighlighted(false);
				setIsSwapWithItemCandidate(false);
				clearNurseHighlight(nurseData.id);
				setSwapNurseId(-1);
				setSwapNurseWithId(-1);
				setSwapDayNo(-1);
				setSwapDayNoWith(-1);
				setSwapShiftNo(-1);
				setSwapShiftNoWith(-1);
			} else if (swapNurseId !== -1 && nurseInShift.nurseId !== swapNurseId) {
				setSwapDayNoWith(dayNo);
				setSwapShiftNoWith(shiftNo);
				setSwapNurseWithId(nurseInShift.nurseId);
				handleMouseLeave();
			} else {
				setSwapDayNo(dayNo);
				setSwapShiftNo(shiftNo);
				setSwapNurseId(nurseInShift.nurseId);
			}
		};
		const handleMenuCloseReminder = useCallback(() => {
			setMenuOpenReminder(false);
			setAnchorEl(null);
		}, []);

		const handleEditMessageChangeReminder = useCallback<ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>>((event) => {
			setEditMessageReminder(event.target.value.trim());
		}, []);

		const checkIfMouseOverElement = useCallback(() => {
			if (isAnyMenuOpen.current) return;

			const target = elementRef.current;
			if (target && mousePosition.x !== null && mousePosition.y !== null) {
				const rect = target.getBoundingClientRect();
				const isOver = mousePosition.x >= rect.left && mousePosition.x <= rect.right && mousePosition.y >= rect.top && mousePosition.y <= rect.bottom;
				setIsMouseOver(isOver);
			}
		}, []);

		const updateMousePosition = useCallback(
			(event: any) => {
				mousePosition = { x: event.clientX, y: event.clientY };
				checkIfMouseOverElement();
			},
			[checkIfMouseOverElement],
		);

		useEffect(() => {
			window.addEventListener("mousemove", updateMousePosition, {
				passive: true,
			});
			const interval = setInterval(checkIfMouseOverElement, 100);
			return () => {
				window.removeEventListener("mousemove", updateMousePosition);
				clearInterval(interval);
			};
		}, [updateMousePosition, checkIfMouseOverElement]);

		useEffect(() => {
			if (isMouseOver) {
				handleMouseEnter();
			} else {
				handleMouseLeave();
			}
		}, [isMouseOver]);

		useEffect(() => {
			if (!isAutoScheduler && compareMessages(editMessage, nurseInShift.message)) {
				addMessageToNurse(date, shiftType, nurseInShift.nurseId, editMessage);
			}
		}, [editMessage]);

		const shouldShowButtons = useCallback(() => {
			return (shouldHighlight || shouldSwapHighlight) && !isSwapWithItemCandidate;
		}, [nurseData.id, shouldHighlight, shouldSwapHighlight, isSwapWithItemCandidate, isMouseOver]);

		const handleSendReminder = async () => {
			const response = await axiosInstance.post("/staffing/send-reminder", {
				message: editMessageReminder,
				nurseId: nurseInShift.nurseId,
			});
			if (response.status === 200) {
				showSnackbar(t("reminder.sent.successfully"), "success");
			} else {
				showErrorSnackbar(
					"Failed to send reminder",
					{
						action: "failed to send reminder",
						nurse_id: nurseInShift.nurseId,
					},
					response.data,
				);
			}
		};

		const getImage = useCallback(async () => {
			const cachedImage = localStorage.getItem(`profile_image_${nurseData.id}`);
			if (cachedImage) {
				setImageUrl(cachedImage);
				return;
			}
			try {
				const response = await axiosInstance.get(`/images/get_profile_image_base64/${nurseData.id}`);
				const imageData = `data:image/jpeg;base64,${response.data}`;
				localStorage.setItem(`profile_image_${nurseData.id}`, imageData);
				setImageUrl(imageData);
			} catch (e) {
				showErrorSnackbar(
					"Failed to fetch profile image",
					{
						action: "failed to fetch profile image",
						nurse_id: nurseData.id,
					},
					e,
				);
				console.log(e);
			}
		}, [nurseData.id, showSnackbar]);

		useEffect(() => {
			if (nurseData?.profileImage) {
				getImage();
			}
		}, [nurseData?.profileImage]);

		const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			setActionsMenuAnchorEl(event.currentTarget);
			isAnyMenuOpen.current = true;
		};

		const handleActionsMenuClose = () => {
			setActionsMenuAnchorEl(null);
			isAnyMenuOpen.current = false;
		};

		// The hospital demo's draft weeks are counted by position in the old list; a week it doesn't hold isn't a draft week.
		const staffSchedulePath = useStaffSchedulePath();
		const draftModeEnabled = useMemo(() => checkIsDraftMode(getWeekIndexByDate(date)) && isHospitalDemo, [getWeekIndexByDate, date, isHospitalDemo]);

		return (
			<div
				ref={elementRef}
				onDoubleClick={() => {
					navigate(staffSchedulePath(nurseInShift.nurseId, draftModeEnabled));
				}}
			>
				{menuOpenReminder && (
					<Menu
						onClose={handleMenuCloseReminder}
						anchorEl={anchorEl}
						keepMounted
						open={menuOpenReminder}
						anchorOrigin={{
							vertical: "bottom", // Menu grows downward from the bottom of the anchor element
							horizontal: "right", // Aligns to the right of the anchor element
						}}
						transformOrigin={{
							vertical: "top", // Transforms the menu position starting from the top
							horizontal: "left", // Aligns the transformed position to the left
						}}
						style={{ zIndex: 999999 }}
					>
						<Grid
							container
							sx={{
								flexDirection: "row",
								justifyContent: "flex-end",
								alignItems: "center",
							}}
						>
							<TextField
								multiline
								sx={{ m: 1 }}
								fullWidth
								autoFocus
								variant="standard"
								value={editMessageReminder}
								onChange={handleEditMessageChangeReminder}
							/>

							<HeaderButton
								disabled={false}
								text="Send Reminder"
								state={"on"}
								onPress={() => {
									handleSendReminder();
									handleMenuCloseReminder();
								}}
							/>
						</Grid>
					</Menu>
				)}
				{nurseInShift && (
					<div
						data-testid={`staffing-nurse-chip-${nurseData.id}`}
						className={`${Style.frame}
                     ${(shouldHighlight || shouldSwapHighlight) && !isPending ? Style.highlighted : isSwapWithItemCandidate ? Style.swapHighlighted : ""}
                      ${isPending && isPublished ? Style.pending : isPending ? Style.pendingNotPublished : ""}`}
						style={
							!isMobile
								? (nurseInShift.nurseId === swapNurseId && swapNurseId !== -1) ||
									!inDifferentGroup ||
									isDayIncludesNurse ||
									isExistInSwap ||
									(swappedNurseRole && !swappedRoleMatch)
									? { cursor: "not-allowed" }
									: {}
								: { paddingTop: "25px", paddingBottom: "25px" }
						}
						ref={elementRef}
						onClick={() => {
							if (
								swapNurseId !== -1 &&
								swapNurseId !== nurseInShift.nurseId &&
								!isDayIncludesNurse &&
								!isPendingToDelete &&
								!isExistInSwap &&
								swappedRoleMatch
							) {
								onSwapClick();
							}
						}}
					>
						{actionsMenuAnchorEl && (
							<NormalizedNurseMenu
								nurseInShift={nurseInShift}
								menuAnchorElement={actionsMenuAnchorEl}
								source={ChangesSource.Staffing}
								onClose={handleActionsMenuClose}
								showActual={false}
								handleAcceptChanges={handleAcceptChanges}
								handleDeclineChanges={handleDeclineChanges}
							/>
						)}

						<div className={Style.name}>
							<div
								style={{
									maxWidth: "calc(100% - 10px)",
									overflow: "hidden",
									textDecoration: isPendingToDelete ? "line-through" : "none",
									position: "relative",
								}}
							>
								<div style={{ display: "flex", alignItems: "center" }}>
									{isPending && (
										<span>
											{isPendingToDelete && (
												<Delete
													sx={{
														color: "tomato",
														fontSize: "27px",
														paddingTop: "5px",
													}}
												/>
											)}
										</span>
									)}
									{isSnf ? (
										<Chip
											label={nurseData.role.match(/\((.*?)\)/)?.[1] || nurseData.role}
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												mr: 0.4,
												height: 16,
												color: INK.SECONDARY,
												backgroundColor: roleColor(nurseData.role).fill,
												fontSize: "11px",
												fontWeight: 600,
												"& .MuiChip-label": { px: "6px" },
												minWidth: "37px",
												width: "fit-content",
											}}
										/>
									) : (
										<Avatar src={imageUrl ? imageUrl : "/default-profile.png"} sx={{ width: 24, height: 24, marginRight: "8px" }} />
									)}
									<span
										style={{
											color: (shouldHighlight || shouldSwapHighlight) && !isPending ? SURFACE : "inherit",
										}}
									>
										{formatPersonName(nurseData)}
									</span>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginLeft: "auto",
											gap: "4px",
										}}
									>
										{nurseInShift.charge ? (
											<MuiTooltip title={t("tooltip.nurse.shiftManager")}>
												<ThemedSvg src="/plus-medical.svg" alt="Medical Plus" color="var(--inhouse-primary)" />
											</MuiTooltip>
										) : canBeReliefCharge ? (
											<MuiTooltip title={t("tooltip.nurse.canBeReliefCharge")}>
												<ThemedSvg src="/plus-medical.svg" alt="Medical Plus" color="var(--inhouse-primary)" />
											</MuiTooltip>
										) : null}
										{nurseInShift.trainingStatus === NurseTrainingStatus.Trainee && (
											<MuiTooltip title={t("tooltip.nurse.trainee")}>
												<Box sx={{ display: "inline-flex" }}>
													<ThemedSvg src="/student.svg" alt="Trainee" size={15} color="var(--inhouse-primary)" />
												</Box>
											</MuiTooltip>
										)}
										{nurseInShift.trainingStatus === NurseTrainingStatus.Education && (
											<MuiTooltip title={t("tooltip.nurse.education")}>
												<CastForEducation
													aria-label={t("education.icon")}
													sx={{
														color: INK.SECONDARY,
														width: 15,
														height: 15,
													}}
												/>
											</MuiTooltip>
										)}
										{!isDefaultShiftTime(nurseInShift, chosenDepartment) && (
											<MuiTooltip title={`${nurseInShift?.startTime.toFormat("h:mm a")} - ${nurseInShift?.endTime.toFormat("h:mm a")}`} arrow>
												<img
													src={"/partial_shift.svg"}
													alt="Partial Shift"
													style={{
														width: "16px",
														height: "16px",
														color: `${alpha(INK.SECONDARY, 0.6)}`,
													}}
												/>
											</MuiTooltip>
										)}
										{nurseInShift.recurringShiftId && (
											<InHouseTooltip title={"Recurring Shift"}>
												<AutorenewIcon style={{ color: alpha(INK.SECONDARY, 0.5), fontSize: "18px" }} />
											</InHouseTooltip>
										)}
									</div>
								</div>
							</div>
						</div>
						<Box
							sx={{
								padding: "2px",
								position: "absolute",
								right: "0",
								top: "12px",
								display: "flex",
							}}
						>
							<IconButton sx={{ bottom: isMobile ? "4px" : "14px" }} size="small" onClick={handleActionsMenuOpen} disabled={!shouldShowButtons()}>
								<MoreVert sx={{ fontSize: "20px" }} />
							</IconButton>
						</Box>
					</div>
				)}
			</div>
		);
	},
);

export const DebuggedNurseContainer = withPropsDebugging(NurseContainer, "NurseContainer");
